import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, CalendarX, Trash2, Lock } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import api from "@/lib/api";

export default function ManageCalendarPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [space, setSpace] = useState(null);
  const [blockedDates, setBlockedDates] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [dateRange, setDateRange] = useState(undefined);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get(`/spaces/${id}`).then(r => setSpace(r.data)),
      api.get(`/spaces/${id}/blocked-dates`).then(r => setBlockedDates(r.data)),
      api.get(`/spaces/${id}/availability`).then(r => setBookings(r.data)),
    ]).catch(() => toast.error("Erreur de chargement")).finally(() => setLoading(false));
  }, [id]);

  const getDisabledDates = () => {
    const disabled = [];
    blockedDates.forEach(b => {
      try {
        const start = new Date(b.start_date);
        const end = new Date(b.end_date);
        for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1)) {
          disabled.push(new Date(d));
        }
      } catch (err) { console.error("Invalid blocked date range:", b, err); }
    });
    return disabled;
  };

  const getBookedDates = () => {
    const booked = [];
    bookings.forEach(b => {
      try {
        const start = new Date(b.check_in);
        const end = new Date(b.check_out);
        for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1)) {
          booked.push(new Date(d));
        }
      } catch (err) { console.error("Invalid booking date range:", b, err); }
    });
    return booked;
  };

  const handleBlock = async () => {
    if (!dateRange?.from || !dateRange?.to) { toast.error("Sélectionnez une plage de dates"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/spaces/${id}/blocked-dates`, {
        start_date: dateRange.from.toISOString(),
        end_date: dateRange.to.toISOString(),
        reason,
      });
      setBlockedDates(prev => [...prev, data]);
      setDateRange(undefined);
      setReason("");
      toast.success("Dates bloquees!");
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const handleUnblock = async (blockId) => {
    try {
      await api.delete(`/spaces/${id}/blocked-dates/${blockId}`);
      setBlockedDates(prev => prev.filter(b => b.id !== blockId));
      toast.success("Blocage supprime");
    } catch { toast.error("Erreur"); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-4 border-zinc-300 border-t-emerald-700 rounded-full" /></div>;

  const disabledDates = getDisabledDates();
  const bookedDates = getBookedDates();

  // Custom modifiers for calendar styling
  const modifiers = {
    blocked: disabledDates,
    booked: bookedDates,
  };
  const modifiersStyles = {
    blocked: { backgroundColor: "#fecaca", color: "#991b1b", borderRadius: "4px" },
    booked: { backgroundColor: "#d1fae5", color: "#065f46", borderRadius: "4px" },
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 animate-fade-in">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate("/my-spaces")} data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></Button>
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-zinc-950" data-testid="manage-calendar-title">Calendrier - {space?.title}</h1>
          <p className="text-sm text-zinc-500">Bloquez des dates pour empecher les réservations</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Calendar */}
        <Card className="border border-zinc-200 rounded-2xl" data-testid="block-calendar-card">
          <CardHeader><CardTitle className="font-heading text-lg">Bloquer des dates</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-3 text-xs mb-2">
              <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-red-200 inline-block" /> Bloque</span>
              <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-emerald-200 inline-block" /> Reserve</span>
            </div>
            <Calendar
              mode="range"
              selected={dateRange}
              onSelect={setDateRange}
              disabled={{ before: new Date() }}
              modifiers={modifiers}
              modifiersStyles={modifiersStyles}
              numberOfMonths={1}
              className="rounded-xl border border-zinc-200"
              data-testid="block-calendar"
            />
            {dateRange?.from && dateRange?.to && (
              <p className="text-sm text-zinc-600">
                {format(dateRange.from, "d MMM", { locale: fr })} - {format(dateRange.to, "d MMM yyyy", { locale: fr })}
              </p>
            )}
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Raison (optionnel)" className="rounded-xl" data-testid="block-reason-input" />
            <Button onClick={handleBlock} disabled={saving || !dateRange?.from || !dateRange?.to} className="w-full bg-red-600 hover:bg-red-700 text-white rounded-xl" data-testid="block-dates-btn">
              <Lock className="h-4 w-4 mr-2" /> {saving ? "Blocage..." : "Bloquer ces dates"}
            </Button>
          </CardContent>
        </Card>

        {/* Blocked periods list */}
        <Card className="border border-zinc-200 rounded-2xl" data-testid="blocked-list-card">
          <CardHeader><CardTitle className="font-heading text-lg">Périodes bloquees</CardTitle></CardHeader>
          <CardContent>
            {blockedDates.length === 0 ? (
              <div className="text-center py-8" data-testid="no-blocked-dates">
                <CalendarX className="h-10 w-10 text-zinc-300 mx-auto mb-3" />
                <p className="text-sm text-zinc-500">Aucune date bloquee</p>
              </div>
            ) : (
              <div className="space-y-3">
                {blockedDates.map((b, i) => (
                  <div key={b.id} className="flex items-center justify-between p-3 rounded-xl bg-red-50 border border-red-100" data-testid={`blocked-period-${i}`}>
                    <div>
                      <p className="text-sm font-medium text-red-800">
                        {format(new Date(b.start_date), "d MMM", { locale: fr })} - {format(new Date(b.end_date), "d MMM yyyy", { locale: fr })}
                      </p>
                      {b.reason && <p className="text-xs text-red-600 mt-0.5">{b.reason}</p>}
                    </div>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-600" onClick={() => handleUnblock(b.id)} data-testid={`unblock-${i}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {bookings.length > 0 && (
              <div className="mt-6">
                <h3 className="font-medium text-zinc-700 text-sm mb-3">Réservations existantes</h3>
                <div className="space-y-2">
                  {bookings.map((b, i) => (
                    <div key={i} className="p-2 rounded-lg bg-emerald-50 border border-emerald-100 text-sm text-emerald-800">
                      {format(new Date(b.check_in), "d MMM", { locale: fr })} - {format(new Date(b.check_out), "d MMM yyyy", { locale: fr })}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
